/**
 * Choreography operators + player (TOP-10 / CHOREO).
 * See docs/architecture/DESIGNS-TRACK-X.md §4.
 *
 * Scripts are short timed recipes that mutate the live sim (freeze graph,
 * rewire friends, nudge steps, …). Selecting a script in the transport bar
 * loads and runs it — same “pick = apply” pattern as presets.
 */

import { applyFreezeTopology, applyRechooseProb } from "../sim/freezeCoupling";
import type { Simulation } from "../sim/simulation";

export type ChoreographyOp =
  | { op: "freeze"; value: boolean }
  | { op: "rewire_friend"; i: number; j: number }
  | { op: "rewire_enemy"; i: number; j: number }
  | { op: "nudge_steps"; friendDelta: number; enemyDelta: number }
  | { op: "set_rechoose"; value: number }
  | { op: "wait_ticks"; n: number }
  | { op: "reseed"; seed?: number }
  | { op: "reset" }
  | { op: "disturb_random_friend" };

export type ChoreographyTarget = {
  applyOp(op: ChoreographyOp): void;
};

/** Built-in demo script shown in the transport Script menu. */
export type ChoreographyScript = {
  id: string;
  /** Short menu label */
  label: string;
  /** One line — option title + status strip */
  summary: string;
  /** Longer teachable blurb (details panel) */
  detail: string;
  /** Rough duration hint for the menu */
  durationHint?: string;
  ops: ChoreographyOp[];
};

/** Adapt Simulation to choreography without circular imports at type level. */
export function simulationTarget(sim: Simulation): ChoreographyTarget {
  return {
    applyOp(op: ChoreographyOp) {
      switch (op.op) {
        case "freeze":
          sim.params = applyFreezeTopology(sim.params, op.value);
          break;
        case "rewire_friend":
          sim.rewireFriend(op.i, op.j);
          break;
        case "rewire_enemy":
          sim.rewireEnemy(op.i, op.j);
          break;
        case "nudge_steps":
          sim.params.friendStep = clampStep(
            sim.params.friendStep + op.friendDelta,
          );
          sim.params.enemyStep = clampStep(
            sim.params.enemyStep + op.enemyDelta,
          );
          break;
        case "set_rechoose":
          sim.params = applyRechooseProb(sim.params, op.value);
          break;
        case "reseed":
          sim.reseed(op.seed);
          break;
        case "reset":
          sim.reset();
          break;
        case "disturb_random_friend":
          sim.disturbRandomFriend();
          break;
        case "wait_ticks":
          break; // handled by player
      }
    },
  };
}

function clampStep(v: number): number {
  return Math.max(0, Math.min(0.5, v));
}

/** UI-facing lifecycle for a loaded script. */
export type ChoreographyStatus = "idle" | "running" | "done";

export class ChoreographyPlayer {
  private queue: ChoreographyOp[] = [];
  private waitLeft = 0;
  private target: ChoreographyTarget;
  /** Last loaded script id — kept after finish so the menu can stay sticky. */
  scriptId: string | null = null;
  scriptLabel: string | null = null;
  /** idle = nothing loaded; running = queue/wait active; done = finished (selection kept). */
  status: ChoreographyStatus = "idle";

  constructor(target: ChoreographyTarget) {
    this.target = target;
  }

  get remaining(): number {
    return this.queue.length + (this.waitLeft > 0 ? 1 : 0);
  }

  /** True while ops or waits are still outstanding. */
  get active(): boolean {
    return this.status === "running" && this.remaining > 0;
  }

  /** Drop queue and forget selection (user picked “Script…”). */
  clear(): void {
    this.queue = [];
    this.waitLeft = 0;
    this.scriptId = null;
    this.scriptLabel = null;
    this.status = "idle";
  }

  /** Abort only the run; keep scriptId/label as done so the select stays put. */
  stop(): void {
    this.queue = [];
    this.waitLeft = 0;
    if (this.scriptId) this.status = "done";
    else this.status = "idle";
  }

  load(ops: ChoreographyOp[], meta?: { id?: string; label?: string }): void {
    this.queue = ops.slice();
    this.waitLeft = 0;
    this.scriptId = meta?.id ?? null;
    this.scriptLabel = meta?.label ?? null;
    this.status = "running";
  }

  /**
   * Apply leading non-wait ops immediately (freeze, disturb, reset, …).
   * Stops at the first wait_ticks so timed recipes still need sim steps.
   * Call after load so paused exhibits still feel the first beat.
   * Marks status done if the whole script was instant.
   */
  pumpImmediate(): void {
    if (this.waitLeft > 0) return;
    while (this.queue.length > 0) {
      const next = this.queue[0]!;
      if (next.op === "wait_ticks") {
        this.queue.shift();
        this.waitLeft = Math.max(0, next.n | 0);
        this.finishIfEmpty();
        return;
      }
      this.queue.shift();
      this.target.applyOp(next);
    }
    this.finishIfEmpty();
  }

  /** Call once per sim step (after model step). */
  tick(): void {
    if (this.status !== "running") return;
    if (this.waitLeft > 0) {
      this.waitLeft--;
      this.finishIfEmpty();
      return;
    }
    while (this.queue.length > 0) {
      const op = this.queue.shift()!;
      if (op.op === "wait_ticks") {
        this.waitLeft = Math.max(0, op.n | 0);
        this.finishIfEmpty();
        return;
      }
      this.target.applyOp(op);
    }
    this.finishIfEmpty();
  }

  private finishIfEmpty(): void {
    if (this.queue.length === 0 && this.waitLeft <= 0) {
      this.status = this.scriptId ? "done" : "idle";
    }
  }
}

/**
 * Built-in demo scripts for the transport Script menu.
 * Selecting an entry auto-runs it (no separate Run button).
 */
export const BUILTIN_SCRIPTS: ChoreographyScript[] = [
  {
    id: "freeze_and_nudge",
    label: "Freeze + nudge steps",
    summary: "Lock who-is-friend, briefly raise friend step, then unfreeze.",
    detail:
      "Freezes the friend/enemy graph so rechoose stops. After a short wait, nudges friend step up and enemy step down — the cloud keeps moving on a fixed social network. Then unfreezes so relationships can rewire again. Great on Classic Dance with friendship edges on.",
    durationHint: "~90 ticks",
    ops: [
      { op: "freeze", value: true },
      { op: "wait_ticks", n: 30 },
      { op: "nudge_steps", friendDelta: 0.005, enemyDelta: -0.002 },
      { op: "wait_ticks", n: 60 },
      { op: "freeze", value: false },
    ],
  },
  {
    id: "disturb_burst",
    label: "Disturb burst ×5",
    summary: "Five spaced random friend rewires — watch structure hiccup.",
    detail:
      "Same action as the Disturb button, five times with pauses. Each hit rewires one particle’s friend pointer to someone else. Patterns wobble then recover. Pair with Color by → Friendship cycle or friend edges.",
    durationHint: "~75 ticks",
    ops: [
      { op: "disturb_random_friend" },
      { op: "wait_ticks", n: 15 },
      { op: "disturb_random_friend" },
      { op: "wait_ticks", n: 15 },
      { op: "disturb_random_friend" },
      { op: "wait_ticks", n: 15 },
      { op: "disturb_random_friend" },
      { op: "wait_ticks", n: 15 },
      { op: "disturb_random_friend" },
    ],
  },
  {
    id: "calm_then_wake",
    label: "Calm then wake",
    summary: "Freeze + zero rechoose, wait, then restore lively rewiring.",
    detail:
      "Stops topology change (freeze + r = 0) so the cloud can settle toward an equilibrium dance on a fixed graph. After a pause, sets a moderate rechoose rate and unfreezes — relationships wake up and patterns morph again. Useful on Frozen Equilibrium or any dense cloud.",
    durationHint: "~150 ticks",
    ops: [
      { op: "freeze", value: true },
      { op: "set_rechoose", value: 0 },
      { op: "wait_ticks", n: 90 },
      { op: "set_rechoose", value: 0.15 },
      { op: "freeze", value: false },
      { op: "wait_ticks", n: 60 },
    ],
  },
  {
    id: "step_pulse",
    label: "Friend-step pulse",
    summary: "Briefly boost attraction, then ease it back.",
    detail:
      "Does not touch topology. Raises friend step for a stretch (tighter packing / stronger streams), waits, then drops it again. Enemy step gets a small opposite nudge so the pulse is visible. Try on Swarm of Flies or Classic Dance.",
    durationHint: "~120 ticks",
    ops: [
      { op: "nudge_steps", friendDelta: 0.012, enemyDelta: -0.004 },
      { op: "wait_ticks", n: 45 },
      { op: "nudge_steps", friendDelta: -0.012, enemyDelta: 0.004 },
      { op: "wait_ticks", n: 75 },
    ],
  },
  {
    id: "repel_spike",
    label: "Enemy-step spike",
    summary: "Short burst of repulsion, then restore.",
    detail:
      "Pushes enemy step up so the cloud puffs and spreads, then walks it back. Good contrast to the friend-step pulse — same Woods algebra, opposite social force. Watch radius of gyration if metrics are on.",
    durationHint: "~100 ticks",
    ops: [
      { op: "nudge_steps", friendDelta: 0, enemyDelta: 0.02 },
      { op: "wait_ticks", n: 40 },
      { op: "nudge_steps", friendDelta: 0, enemyDelta: -0.02 },
      { op: "wait_ticks", n: 60 },
    ],
  },
  {
    id: "reset_and_settle",
    label: "Reset positions",
    summary: "Snap positions back to init layout; keep current graph.",
    detail:
      "Calls reset: particles jump back to their initial positions (same seed/layout) while friend/enemy pointers stay as they are unless the init path rebuilds them. A clean “start over geometry” without changing the Script menu selection mid-run. Prefer Reseed if you want a new random world.",
    durationHint: "instant",
    ops: [{ op: "reset" }],
  },
  {
    id: "reseed_fresh",
    label: "Reseed world",
    summary: "New random seed — fresh positions and relationships.",
    detail:
      "Same as the Reseed button: new RNG seed, new layout and topology draw. Use when you want a different cloud under the same sliders. Script ends immediately after the reseed.",
    durationHint: "instant",
    ops: [{ op: "reseed" }],
  },
  {
    id: "freeze_only",
    label: "Freeze graph",
    summary: "Lock friend/enemy pointers (no more rechoose).",
    detail:
      "One-shot freeze. Motion continues; the digraph does not rewire. Toggle Freeze topology in the panel, or run Calm then wake / Freeze + nudge to reverse later. Ideal before studying cycle color or edge lenses.",
    durationHint: "instant",
    ops: [{ op: "freeze", value: true }],
  },
  {
    id: "unfreeze",
    label: "Unfreeze graph",
    summary: "Allow relationships to rewire again.",
    detail:
      "Clears freeze so rechoose (if r > 0) can change friends and enemies. Pair with a non-zero Rechoose probability slider if nothing seems to change after unfreezing.",
    durationHint: "instant",
    ops: [{ op: "freeze", value: false }],
  },
  {
    id: "double_disturb",
    label: "Double disturb",
    summary: "Two quick friend rewires back-to-back.",
    detail:
      "Faster than Disturb burst — two random friend rewires with almost no wait. A light poke when a full burst feels too long.",
    durationHint: "~8 ticks",
    ops: [
      { op: "disturb_random_friend" },
      { op: "wait_ticks", n: 8 },
      { op: "disturb_random_friend" },
    ],
  },
];

export function getChoreographyScript(
  id: string,
): ChoreographyScript | undefined {
  return BUILTIN_SCRIPTS.find((s) => s.id === id);
}

/** Plain-language line for the Disturb transport control. */
export const DISTURB_HELP = {
  label: "Disturb",
  summary: "Rewire one random particle’s friend to someone else.",
  detail:
    "Picks a random particle and assigns it a new friend index. One edge in the social digraph flips; positions are unchanged that instant, but the Woods step will pull that particle toward a new target next frames. Does not touch enemies. Repeat or run “Disturb burst” for a stronger hiccup. Best watched with friend edges or Friendship cycle color.",
} as const;
